import { format } from 'date-fns'
import { ref, type Ref } from 'vue'
import { DAYS_FOR_DOCTOR_SLOTS } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsSchedule/constants'
import apiDoctorSlots from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/api/apiDoctorSlots'
import mapDoctorSlots from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/functions/mapDoctorSlots'
import getTownTimedelta from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/getTownTimedelta'
import type { LpuDoctor } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuDoctorsList/types'
import type { DoctorSlotsByDate } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuScheduleCalendar/types'
import tDynamic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tDynamic'

/**
 * Массовая загрузка слотов клиники для карточек врачей.
 *
 * Первый кадр — карточки из SSR-контекста; слоты подгружаются на клиенте.
 */
export default function useDoctorsListSchedule(payload: {
  lpuId: number
  townTimedeltaCandidates: Array<number | string | null | undefined>
  doctors: Ref<LpuDoctor[]>
}) {
  const slotsByDoctorId = ref<Record<number, DoctorSlotsByDate>>({})
  const isLoading = ref(payload.doctors.value.some(doctor => (
    doctor.workplace_info?.appointment?.has_slots
    && doctor.workplace_info.appointment.is_on
  )))

  async function loadSlots() {
    const doctorsForRequest = payload.doctors.value.filter(doctor => (
      doctor.workplace_info?.appointment?.has_slots
      && doctor.workplace_info.appointment.is_on
    ))

    if (!doctorsForRequest.length) {
      isLoading.value = false

      return
    }

    isLoading.value = true

    try {
      const { data } = await apiDoctorSlots.post({
        days: DAYS_FOR_DOCTOR_SLOTS,
        dt_start: format(new Date(), 'yyyy-MM-dd'),
        town_timedelta: getTownTimedelta(payload.townTimedeltaCandidates),
        doctors_lpus: doctorsForRequest.map(doctor => ({
          lpu_id: payload.lpuId,
          doctor_id: doctor.id,
          doctor_fio: tDynamic(doctor, 'fio') || tDynamic(doctor, 'fio_short'),
        })),
      })

      slotsByDoctorId.value = (data.result || []).reduce<Record<number, DoctorSlotsByDate>>(
        (acc, item) => {
          acc[item.doctor_id] = mapDoctorSlots(
            item.slots as Record<string, Array<{ time: string, free?: boolean, duration?: number }>>,
            item.doctor_id,
          )

          return acc
        },
        {},
      )
    } catch {
      slotsByDoctorId.value = {}
    } finally {
      isLoading.value = false
    }
  }

  return {
    slotsByDoctorId,
    isLoading,
    loadSlots,
  }
}
